import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Target, Upload, ArrowLeft, ArrowRight, Check, ChevronRight, Send, MessageCircle, Linkedin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HelpPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const chatInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  
  const [formData, setFormData] = useState({
    current_job_title: "",
    current_industry: "",
    target_role: "",
    target_industry: "",
    experience_level: "",
    career_goals: "",
    specific_concerns: "",
    linkedin_headline: "",
    linkedin_bio: "",
    linkedin_experience: ""
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("ANALYZING...");
  const [result, setResult] = useState(null);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);

  const loadingMessages = [
    "ANALYZING YOUR PROFILE...",
    "REVIEWING EXPERIENCE...",
    "EVALUATING STRENGTHS...",
    "FINDING OPPORTUNITIES...",
    "CRAFTING FEEDBACK..."
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return;
    const fileName = selectedFile.name.toLowerCase();
    const isValidType = fileName.endsWith('.pdf') || fileName.endsWith('.docx') || fileName.endsWith('.doc');
    if (!isValidType) {
      toast.error("Please upload a PDF or Word document");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("File size must be under 10MB");
      return;
    }
    setFile(selectedFile);
    toast.success(`File loaded: ${selectedFile.name}`);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.current_job_title && formData.current_industry;
      case 2:
        return formData.target_role && formData.target_industry;
      case 3:
        return formData.experience_level && formData.career_goals;
      case 4:
        return true; // Step 4 is entirely optional
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    let messageIndex = 0;
    const interval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingText(loadingMessages[messageIndex]);
    }, 1500);

    try {
      const formDataObj = new FormData();
      if (file) {
        formDataObj.append('file', file);
      }
      Object.entries(formData).forEach(([key, value]) => {
        formDataObj.append(key, value);
      });

      const response = await axios.post(`${API}/analyze`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      toast.success("Analysis complete!");
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || "Failed to analyze. Please try again.");
    } finally {
      clearInterval(interval);
      setIsLoading(false);
    }
  };

  const handleChatSend = async () => {
    const message = chatInput.trim();
    if (!message || isChatLoading) return;

    const newUserMsg = { role: "user", content: message };
    setChatMessages(prev => [...prev, newUserMsg]);
    setChatInput("");
    setIsChatLoading(true);

    // Build feedback summary from result
    const feedbackSummary = result ? [
      result.overall_impression && `Overall Impression: ${result.overall_impression}`,
      result.formatting && `Formatting: ${result.formatting}`,
      result.content_relevance && `Content Relevance: ${result.content_relevance}`,
      result.strengths && `Strengths: ${result.strengths}`,
      result.areas_to_improve && `Areas to Improve: ${result.areas_to_improve}`,
      result.recommendations && `Recommendations: ${result.recommendations}`
    ].filter(Boolean).join("\n\n") : "";

    try {
      const response = await axios.post(`${API}/chat`, {
        messages: [...chatMessages, newUserMsg],
        questionnaire: formData,
        feedback_summary: feedbackSummary,
        resume_text: ""
      });

      setChatMessages(prev => [...prev, { role: "assistant", content: response.data.content }]);
      
      // Scroll to bottom
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error("Failed to get response. Try again.");
      setChatMessages(prev => [...prev, { role: "assistant", content: "Sorry, I couldn't process that. Please try again." }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      current_job_title: "", current_industry: "",
      target_role: "", target_industry: "",
      experience_level: "", career_goals: "", specific_concerns: "",
      linkedin_headline: "", linkedin_bio: "", linkedin_experience: ""
    });
    setFile(null);
    setResult(null);
    setChatMessages([]);
    setChatInput("");
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-3 block">
                Current Job Title
              </Label>
              <Input
                data-testid="current-job-input"
                placeholder="e.g., Software Engineer, Marketing Manager"
                value={formData.current_job_title}
                onChange={(e) => handleInputChange('current_job_title', e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white placeholder:text-[#666] focus:border-[#32D74B] rounded-none h-12"
              />
            </div>
            <div>
              <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-3 block">
                Current Industry
              </Label>
              <Input
                data-testid="current-industry-input"
                placeholder="e.g., Technology, Healthcare, Finance"
                value={formData.current_industry}
                onChange={(e) => handleInputChange('current_industry', e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white placeholder:text-[#666] focus:border-[#32D74B] rounded-none h-12"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-3 block">
                Target Role
              </Label>
              <Input
                data-testid="target-role-input"
                placeholder="What role are you applying for?"
                value={formData.target_role}
                onChange={(e) => handleInputChange('target_role', e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white placeholder:text-[#666] focus:border-[#32D74B] rounded-none h-12"
              />
            </div>
            <div>
              <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-3 block">
                Target Industry / Company Type
              </Label>
              <Input
                data-testid="target-industry-input"
                placeholder="e.g., Startup, Fortune 500, Non-profit"
                value={formData.target_industry}
                onChange={(e) => handleInputChange('target_industry', e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white placeholder:text-[#666] focus:border-[#32D74B] rounded-none h-12"
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-4 block">
                Experience Level
              </Label>
              <RadioGroup
                value={formData.experience_level}
                onValueChange={(value) => handleInputChange('experience_level', value)}
                className="grid grid-cols-2 gap-4"
                data-testid="experience-level-group"
              >
                {['Entry Level', 'Mid Level', 'Senior', 'Executive'].map((level) => (
                  <div key={level} className="flex items-center">
                    <RadioGroupItem
                      value={level}
                      id={level}
                      className="border-white/20 text-[#32D74B]"
                      data-testid={`experience-${level.toLowerCase().replace(' ', '-')}`}
                    />
                    <Label htmlFor={level} className="ml-3 text-[#F5F5F5] cursor-pointer">
                      {level}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-3 block">
                Career Goals (1-3 Years)
              </Label>
              <Textarea
                data-testid="career-goals-input"
                placeholder="What do you want to achieve in your career?"
                value={formData.career_goals}
                onChange={(e) => handleInputChange('career_goals', e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white placeholder:text-[#666] focus:border-[#32D74B] rounded-none min-h-[100px]"
              />
            </div>
            <div>
              <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-3 block">
                Specific Concerns (Optional)
              </Label>
              <Textarea
                data-testid="concerns-input"
                placeholder="Anything specific you're worried about?"
                value={formData.specific_concerns}
                onChange={(e) => handleInputChange('specific_concerns', e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white placeholder:text-[#666] focus:border-[#32D74B] rounded-none min-h-[80px]"
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-8">
            {/* LinkedIn Section */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Linkedin className="w-5 h-5 text-[#0A66C2]" />
                <h3 className="font-clash text-base font-semibold text-[#F5F5F5] uppercase tracking-wide">
                  Optional: Add Your LinkedIn Profile Info
                </h3>
              </div>
              <p className="text-[#888888] text-sm mb-6">
                The more context you give us, the better your feedback will be.
              </p>

              <div className="space-y-5 pl-0">
                <div>
                  <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-3 block">
                    LinkedIn Headline
                  </Label>
                  <Input
                    data-testid="linkedin-headline-input"
                    placeholder="The short tagline under your name on LinkedIn"
                    value={formData.linkedin_headline}
                    onChange={(e) => handleInputChange('linkedin_headline', e.target.value)}
                    className="bg-[#0A0A0A] border-white/20 text-white placeholder:text-[#666] focus:border-[#0A66C2] rounded-none h-12"
                  />
                </div>
                <div>
                  <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-3 block">
                    LinkedIn Bio / About Section
                  </Label>
                  <Textarea
                    data-testid="linkedin-bio-input"
                    placeholder="Paste your LinkedIn 'About' section here"
                    value={formData.linkedin_bio}
                    onChange={(e) => handleInputChange('linkedin_bio', e.target.value)}
                    className="bg-[#0A0A0A] border-white/20 text-white placeholder:text-[#666] focus:border-[#0A66C2] rounded-none min-h-[100px]"
                  />
                </div>
                <div>
                  <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-3 block">
                    Work Experience Entries
                  </Label>
                  <Textarea
                    data-testid="linkedin-experience-input"
                    placeholder="Paste your job entries: title, company, dates, and description for each role"
                    value={formData.linkedin_experience}
                    onChange={(e) => handleInputChange('linkedin_experience', e.target.value)}
                    className="bg-[#0A0A0A] border-white/20 text-white placeholder:text-[#666] focus:border-[#0A66C2] rounded-none min-h-[140px]"
                  />
                  <p className="text-[#666] text-xs mt-2 italic">
                    Tip: Copy and paste directly from each role on your LinkedIn profile
                  </p>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-white/10" />

            {/* Optional Resume Upload */}
            <div>
              <Label className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-3 block flex items-center gap-2">
                Upload Resume <span className="text-[#888888] text-xs normal-case tracking-normal">(optional — adds more context)</span>
              </Label>
              <div
                data-testid="help-upload-zone"
                className={`upload-area py-6 ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  data-testid="help-file-input"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <Check className="w-5 h-5 text-[#32D74B]" />
                    <p className="text-[#F5F5F5] text-sm" data-testid="help-file-name">{file.name}</p>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      className="text-[#888888] hover:text-[#FF3B30] text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    <Upload className="w-5 h-5 text-[#888888]" />
                    <p className="text-[#888888] text-sm">Drop resume here or click to browse</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const stepTitles = [
    "Current Situation",
    "Target Goals",
    "Experience & Aspirations",
    "LinkedIn & Resume (Optional)"
  ];

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      <div className="absolute inset-0 grid-overlay pointer-events-none" />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Nav */}
        <nav className="w-full py-6 px-6 md:px-12 border-b border-white/10 bg-black/40 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#888888] hover:text-white transition-colors"
              data-testid="help-back-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm tracking-wide">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <Target className="w-6 h-6 text-[#32D74B]" />
              <span className="font-clash text-xl font-bold tracking-tight">
                TORCHED<span className="text-[#FF3B30]">.CV</span>
              </span>
            </div>
            <div className="w-20" />
          </div>
        </nav>

        {/* Main content */}
        <main className="flex-1 px-6 md:px-12 py-12">
          <div className="w-full max-w-2xl mx-auto">
            {!result ? (
              <div className="animate-fade-in">
                <div className="text-center mb-8">
                  <h1 className="font-clash text-4xl sm:text-5xl font-bold tracking-tight uppercase mb-4">
                    <span className="text-[#32D74B]">Help</span> Mode
                  </h1>
                  <p className="text-[#888888] text-sm tracking-widest uppercase">
                    Honest feedback that actually helps
                  </p>
                </div>

                {/* Progress indicator */}
                <div className="flex items-center justify-center mb-12">
                  {[1, 2, 3, 4].map((step, index) => (
                    <div key={step} className="flex items-center">
                      <div
                        className={`progress-step ${currentStep >= step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
                        data-testid={`step-${step}`}
                      >
                        {currentStep > step ? <Check className="w-5 h-5" /> : step}
                      </div>
                      {index < 3 && (
                        <div className={`progress-line w-10 sm:w-16 ${currentStep > step ? 'completed' : ''}`} />
                      )}
                    </div>
                  ))}
                </div>

                {/* Step title */}
                <div className="text-center mb-8">
                  <p className="text-[#888888] text-xs tracking-[0.3em] uppercase mb-2">
                    Step {currentStep} of {totalSteps}
                  </p>
                  <h2 className="font-clash text-xl font-semibold text-[#F5F5F5]">
                    {stepTitles[currentStep - 1]}
                  </h2>
                </div>

                {/* Form content */}
                <div className="bg-[#111111] border border-white/10 p-6 md:p-8 mb-8">
                  {renderStep()}
                </div>

                {/* Navigation buttons */}
                <div className="flex gap-4">
                  {currentStep > 1 && (
                    <button
                      onClick={handleBack}
                      className="flex-1 py-4 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                      data-testid="step-back-btn"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </button>
                  )}
                  
                  {currentStep < totalSteps ? (
                    <div className="flex-1 flex gap-3">
                      {/* On step 3, show both Next and Submit */}
                      {currentStep === 3 ? (
                        <>
                          <button
                            onClick={handleSubmit}
                            disabled={!canProceed() || isLoading}
                            className={`flex-1 py-4 bg-[#32D74B] text-black font-bold uppercase tracking-wide transition-all flex items-center justify-center gap-2 ${
                              !canProceed() || isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#3DE055]'
                            }`}
                            data-testid="submit-analyze-btn"
                          >
                            {isLoading ? (
                              <span className="font-mono animate-pulse text-sm">{loadingText}</span>
                            ) : (
                              <>
                                <Target className="w-4 h-4" />
                                Get My Career Advice
                              </>
                            )}
                          </button>
                          <button
                            onClick={handleNext}
                            disabled={!canProceed() || isLoading}
                            className={`py-4 px-6 border border-white/20 text-white text-sm font-medium transition-colors flex items-center gap-2 ${
                              !canProceed() || isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/5'
                            }`}
                            data-testid="step-next-btn"
                          >
                            <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                            <span className="hidden sm:inline">Add LinkedIn</span>
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={handleNext}
                          disabled={!canProceed()}
                          className={`flex-1 py-4 bg-[#32D74B] text-black font-bold uppercase tracking-wide transition-all flex items-center justify-center gap-2 ${
                            !canProceed() ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#3DE055]'
                          }`}
                          data-testid="step-next-btn"
                        >
                          Next
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={isLoading}
                      className={`flex-1 py-4 bg-[#32D74B] text-black font-bold uppercase tracking-wide transition-all flex items-center justify-center gap-2 ${
                        isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#3DE055]'
                      }`}
                      data-testid="submit-analyze-btn-step4"
                    >
                      {isLoading ? (
                        <span className="font-mono animate-pulse">{loadingText}</span>
                      ) : (
                        <>
                          <Target className="w-4 h-4" />
                          Get My Career Advice
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              // Results Section
              <div className="animate-slide-up">
                <div className="text-center mb-8">
                  <h1 className="font-clash text-3xl sm:text-4xl font-bold tracking-tight uppercase mb-2">
                    Your <span className="text-[#32D74B]">Feedback</span>
                  </h1>
                  <p className="text-[#888888] text-sm">
                    Honest advice from someone who wants you to succeed
                  </p>
                </div>

                {/* Feedback sections */}
                <div className="space-y-6">
                  {[
                    { key: 'overall_impression', title: 'Overall Impression', icon: '01' },
                    { key: 'formatting', title: 'Formatting & Presentation', icon: '02' },
                    { key: 'content_relevance', title: 'Content & Relevance', icon: '03' },
                    { key: 'strengths', title: 'Strengths', icon: '04' },
                    { key: 'areas_to_improve', title: 'Areas to Improve', icon: '05' },
                    { key: 'recommendations', title: 'Specific Recommendations', icon: '06' }
                  ].map((section) => (
                    result[section.key] && (
                      <div 
                        key={section.key}
                        className="bg-[#111111] border border-white/10 p-6"
                        data-testid={`feedback-${section.key}`}
                      >
                        <div className="flex items-center gap-4 mb-4">
                          <span className="font-mono text-xs text-[#32D74B]">{section.icon}</span>
                          <h3 className="font-clash text-sm uppercase tracking-wide text-[#F5F5F5]">
                            {section.title}
                          </h3>
                        </div>
                        <div className="text-[#888888] text-sm leading-relaxed whitespace-pre-wrap pl-8">
                          {result[section.key]}
                        </div>
                      </div>
                    )
                  ))}
                </div>

                {/* Chat Interface */}
                <div className="mt-10 border-t border-white/10 pt-8" data-testid="chat-section">
                  <div className="flex items-center gap-3 mb-6">
                    <MessageCircle className="w-5 h-5 text-[#32D74B]" />
                    <h2 className="font-clash text-lg font-semibold text-[#F5F5F5] uppercase tracking-wide">
                      Chat With Your Results
                    </h2>
                  </div>
                  <p className="text-[#888888] text-sm mb-6">
                    Ask follow-up questions, request a cover letter, get LinkedIn tips — your AI mentor already knows your full profile.
                  </p>

                  {/* Chat messages */}
                  {chatMessages.length > 0 && (
                    <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto pr-2" data-testid="chat-messages">
                      {chatMessages.map((msg, i) => (
                        <div
                          key={i}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[85%] p-4 text-sm leading-relaxed ${
                              msg.role === 'user'
                                ? 'bg-[#32D74B]/15 border border-[#32D74B]/30 text-[#F5F5F5]'
                                : 'bg-[#111111] border border-white/10 text-[#F5F5F5]'
                            }`}
                            data-testid={`chat-message-${msg.role}-${i}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-mono text-[10px] uppercase tracking-widest text-[#888888]">
                                {msg.role === 'user' ? 'You' : 'Torched.cv AI'}
                              </span>
                            </div>
                            <div className="whitespace-pre-wrap">{msg.content}</div>
                          </div>
                        </div>
                      ))}
                      {isChatLoading && (
                        <div className="flex justify-start">
                          <div className="bg-[#111111] border border-white/10 p-4 text-sm">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-[#888888] block mb-2">Torched.cv AI</span>
                            <span className="text-[#888888] animate-pulse font-mono">Thinking...</span>
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                  )}

                  {/* Chat input */}
                  <div className="flex gap-3">
                    <input
                      ref={chatInputRef}
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={handleChatKeyDown}
                      placeholder="Ask a follow-up question..."
                      className="flex-1 bg-[#0A0A0A] border border-white/20 text-white placeholder:text-[#666] px-4 py-3 text-sm focus:outline-none focus:border-[#32D74B] transition-colors"
                      data-testid="chat-input"
                      disabled={isChatLoading}
                    />
                    <button
                      onClick={handleChatSend}
                      disabled={!chatInput.trim() || isChatLoading}
                      className={`px-5 py-3 bg-[#32D74B] text-black font-bold transition-all flex items-center gap-2 ${
                        !chatInput.trim() || isChatLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#3DE055]'
                      }`}
                      data-testid="chat-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-[#666] text-xs mt-3">
                    Try: "Write me a cover letter" / "How should I rewrite my LinkedIn headline?" / "What companies should I target?"
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4 mt-10">
                  <button
                    onClick={resetForm}
                    className="flex-1 py-4 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
                    data-testid="analyze-again-btn"
                  >
                    Start Over
                  </button>
                  <button
                    onClick={() => navigate('/roast')}
                    className="flex-1 py-4 bg-[#FF3B30] text-white font-bold uppercase tracking-wide hover:bg-[#FF5544] transition-colors flex items-center justify-center gap-2"
                    data-testid="try-roast-btn"
                  >
                    Try Roast Mode Instead
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default HelpPage;
